import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Key, ExternalLink, Check, ChevronRight, ChevronLeft, Copy,
  Loader2, Shield, Zap, X, Search, ArrowRight, Lock, Globe,
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PROVIDER_COLORS = {
  openai: "from-emerald-500/20 to-emerald-600/5 border-emerald-500/30",
  anthropic: "from-orange-500/20 to-orange-600/5 border-orange-500/30",
  gemini: "from-cyan-500/20 to-cyan-600/5 border-cyan-500/30",
  groq: "from-purple-500/20 to-purple-600/5 border-purple-500/30",
  deepseek: "from-blue-500/20 to-blue-600/5 border-blue-500/30",
  cohere: "from-pink-500/20 to-pink-600/5 border-pink-500/30",
  mistral: "from-amber-500/20 to-amber-600/5 border-amber-500/30",
  together: "from-indigo-500/20 to-indigo-600/5 border-indigo-500/30",
  perplexity: "from-teal-500/20 to-teal-600/5 border-teal-500/30",
  fireworks: "from-red-500/20 to-red-600/5 border-red-500/30",
};

export default function BYOKWizard({ onClose }) {
  const [step, setStep] = useState(1); // 1: Choose, 2: Enter key, 3: Confirm
  const [providers, setProviders] = useState({});
  const [platformKeys, setPlatformKeys] = useState([]);
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [keyValue, setKeyValue] = useState("");
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [provRes, keyRes] = await Promise.all([
        axios.get(`${API}/settings/providers`),
        axios.get(`${API}/settings/platform-keys`),
      ]);
      setProviders(provRes.data.providers || {});
      setPlatformKeys(keyRes.data.keys || []);
    } catch {}
  };

  const filteredProviders = Object.entries(providers).filter(([id, p]) =>
    !search || p.name.toLowerCase().includes(search.toLowerCase())
  );

  const handleSaveKey = async () => {
    if (!selectedProvider || !keyValue.trim()) return;
    setSaving(true);
    try {
      await axios.put(`${API}/settings`, {
        [`${selectedProvider}_api_key`]: keyValue.trim(),
      });
      toast.success(`${providers[selectedProvider]?.name} key saved!`);
      setStep(3);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save key");
    }
    setSaving(false);
  };

  const providerStatus = (id) => {
    const pk = platformKeys.find(k => k.provider === id);
    if (!pk) return "none";
    return pk.source;
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="byok-wizard-overlay">
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 20 }}
        className="bg-[var(--qn-card)] border border-[var(--qn-border)] rounded-2xl w-full max-w-2xl max-h-[85vh] overflow-hidden shadow-2xl"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-[var(--qn-border)]">
          <div>
            <h2 className="text-lg font-bold">Add API Key</h2>
            <p className="text-xs text-[var(--qn-foreground-muted)]">
              {step === 1 && "Choose a provider to connect"}
              {step === 2 && `Enter your ${providers[selectedProvider]?.name} API key`}
              {step === 3 && "Key saved successfully!"}
            </p>
          </div>
          <div className="flex items-center gap-3">
            {/* Step indicators */}
            <div className="flex items-center gap-1.5">
              {[1, 2, 3].map(s => (
                <div key={s} className={`w-2 h-2 rounded-full transition-all ${step >= s ? "bg-[var(--qn-primary)] w-5" : "bg-[var(--qn-border)]"}`} />
              ))}
            </div>
            <button onClick={onClose} className="text-[var(--qn-foreground-muted)] hover:text-[var(--qn-foreground)] transition-colors" data-testid="byok-close">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[65vh]">
          <AnimatePresence mode="wait">
            {/* Step 1: Choose Provider */}
            {step === 1 && (
              <motion.div key="step1" initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 20 }}>
                <div className="relative mb-4">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--qn-foreground-muted)]" />
                  <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search providers..." className="pl-10 bg-[var(--qn-background)] border-[var(--qn-border)]" data-testid="byok-search" />
                </div>
                <div className="grid grid-cols-2 gap-3" data-testid="byok-provider-grid">
                  {filteredProviders.map(([id, prov]) => {
                    const status = providerStatus(id);
                    return (
                      <button
                        key={id}
                        onClick={() => { setSelectedProvider(id); setKeyValue(""); setStep(2); }}
                        className={`relative text-left p-4 rounded-xl border bg-gradient-to-br transition-all hover:scale-[1.02] hover:shadow-lg ${PROVIDER_COLORS[id] || "from-gray-500/20 to-gray-600/5 border-gray-500/30"}`}
                        data-testid={`byok-provider-${id}`}
                      >
                        <div className="font-semibold text-sm mb-1">{prov.name}</div>
                        <div className="text-xs text-[var(--qn-foreground-muted)] mb-2">
                          {prov.models?.slice(0, 2).join(", ")}
                          {prov.models?.length > 2 && ` +${prov.models.length - 2}`}
                        </div>
                        <div className="flex items-center gap-1.5">
                          {status === "own" && <Badge className="text-[9px] bg-emerald-500/15 text-emerald-400">Your Key</Badge>}
                          {status === "platform" && <Badge className="text-[9px] bg-blue-500/15 text-blue-400">Platform Key</Badge>}
                          {status === "none" && <Badge className="text-[9px] bg-gray-500/15 text-gray-400">Not Connected</Badge>}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </motion.div>
            )}

            {/* Step 2: Enter Key */}
            {step === 2 && selectedProvider && (
              <motion.div key="step2" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="space-y-5">
                {/* Instructions */}
                <div className={`p-4 rounded-xl border bg-gradient-to-br ${PROVIDER_COLORS[selectedProvider] || ""}`}>
                  <div className="font-semibold mb-2">{providers[selectedProvider]?.name}</div>
                  <div className="text-sm text-[var(--qn-foreground-muted)] space-y-2">
                    <p>{providers[selectedProvider]?.help}</p>
                    <a
                      href={providers[selectedProvider]?.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 text-[var(--qn-primary)] hover:underline text-sm font-medium"
                      data-testid="byok-provider-link"
                    >
                      <ExternalLink className="w-3.5 h-3.5" />
                      Open {providers[selectedProvider]?.name} API Keys Page
                    </a>
                  </div>
                </div>

                {/* Key Input */}
                <div>
                  <label className="text-sm font-medium mb-2 block">API Key</label>
                  <div className="relative">
                    <Key className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--qn-foreground-muted)]" />
                    <Input
                      value={keyValue}
                      onChange={e => setKeyValue(e.target.value)}
                      placeholder={`Paste your ${providers[selectedProvider]?.prefix || ""}... key here`}
                      className="pl-10 pr-4 bg-[var(--qn-background)] border-[var(--qn-border)] font-mono text-sm"
                      type="password"
                      data-testid="byok-key-input"
                      autoFocus
                    />
                  </div>
                  <div className="flex items-center gap-2 mt-2 text-xs text-[var(--qn-foreground-muted)]">
                    <Lock className="w-3 h-3" />
                    Your key is encrypted at rest and never shared. Only you can use it.
                  </div>
                </div>

                {/* Available models */}
                <div>
                  <label className="text-xs font-medium text-[var(--qn-foreground-muted)] mb-1.5 block">Available Models</label>
                  <div className="flex flex-wrap gap-1.5">
                    {providers[selectedProvider]?.models?.map(m => (
                      <Badge key={m} variant="outline" className="text-[10px]">{m}</Badge>
                    ))}
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center justify-between pt-2">
                  <Button variant="ghost" onClick={() => setStep(1)} data-testid="byok-back">
                    <ChevronLeft className="w-4 h-4 mr-1" /> Back
                  </Button>
                  <Button
                    className="bg-[var(--qn-primary)] hover:bg-[var(--qn-primary)]/90"
                    onClick={handleSaveKey}
                    disabled={!keyValue.trim() || saving}
                    data-testid="byok-save-key"
                  >
                    {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Shield className="w-4 h-4 mr-2" />}
                    Save Key
                  </Button>
                </div>
              </motion.div>
            )}

            {/* Step 3: Confirmation */}
            {step === 3 && (
              <motion.div key="step3" initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="text-center py-8 space-y-5">
                <div className="w-16 h-16 mx-auto rounded-full bg-emerald-500/20 flex items-center justify-center">
                  <Check className="w-8 h-8 text-emerald-400" />
                </div>
                <div>
                  <h3 className="text-lg font-bold mb-1">{providers[selectedProvider]?.name} Connected!</h3>
                  <p className="text-sm text-[var(--qn-foreground-muted)]">
                    Your API key has been saved securely. You can now route requests through {providers[selectedProvider]?.name}.
                  </p>
                </div>
                <div className="flex items-center justify-center gap-3">
                  <Button variant="outline" onClick={() => { setStep(1); setSelectedProvider(null); setKeyValue(""); }} data-testid="byok-add-another">
                    Add Another Key
                  </Button>
                  <Button className="bg-[var(--qn-primary)]" onClick={onClose} data-testid="byok-done">
                    Done
                  </Button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </motion.div>
    </div>
  );
}

/* Simplified "My API Keys" card for Settings page */
export function MyAPIKeysCard() {
  const [keys, setKeys] = useState([]);
  const [showWizard, setShowWizard] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchKeys(); }, []);

  const fetchKeys = async () => {
    try {
      const res = await axios.get(`${API}/settings/platform-keys`);
      setKeys(res.data.keys || []);
    } catch {}
    setLoading(false);
  };

  const handleRemoveKey = async (provider) => {
    try {
      await axios.delete(`${API}/settings/api-key/${provider}`);
      toast.success("Key removed");
      fetchKeys();
    } catch {
      toast.error("Failed to remove key");
    }
  };

  const connected = keys.filter(k => k.source !== "none");
  const platform = keys.filter(k => k.source === "platform");

  return (
    <>
      <Card className="border-[var(--qn-border)] bg-[var(--qn-card)]" data-testid="my-api-keys-card">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-base font-outfit flex items-center gap-2">
                <Key className="w-4 h-4 text-[var(--qn-primary)]" />
                API Keys
              </CardTitle>
              <CardDescription className="text-[var(--qn-foreground-muted)] text-xs mt-1">
                {connected.length} provider{connected.length !== 1 ? "s" : ""} connected
                {platform.length > 0 && ` (${platform.length} shared)`}
              </CardDescription>
            </div>
            <Button size="sm" className="bg-[var(--qn-primary)] hover:bg-[var(--qn-primary)]/90" onClick={() => setShowWizard(true)} data-testid="add-key-btn">
              <Key className="w-3.5 h-3.5 mr-1.5" /> Add Key
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-4"><Loader2 className="w-5 h-5 animate-spin text-[var(--qn-primary)]" /></div>
          ) : (
            <div className="space-y-2">
              {keys.map(k => (
                <div key={k.provider} className="flex items-center justify-between py-2 px-3 rounded-lg bg-[var(--qn-background)] border border-[var(--qn-border)]">
                  <div className="flex items-center gap-3">
                    <div className={`w-2 h-2 rounded-full ${k.source === "own" ? "bg-emerald-400" : k.source === "platform" ? "bg-blue-400" : "bg-gray-500"}`} />
                    <span className="text-sm font-medium">{k.name}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    {k.source === "own" && (
                      <>
                        <Badge className="text-[9px] bg-emerald-500/15 text-emerald-400">Your Key</Badge>
                        <button onClick={() => handleRemoveKey(k.provider)} className="text-red-400/60 hover:text-red-400 text-xs" data-testid={`remove-key-${k.provider}`}>
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </>
                    )}
                    {k.source === "platform" && <Badge className="text-[9px] bg-blue-500/15 text-blue-400">Platform (shared)</Badge>}
                    {k.source === "none" && (
                      <button onClick={() => setShowWizard(true)} className="text-[var(--qn-primary)] text-xs hover:underline" data-testid={`connect-${k.provider}`}>Connect</button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <AnimatePresence>
        {showWizard && <BYOKWizard onClose={() => { setShowWizard(false); fetchKeys(); }} />}
      </AnimatePresence>
    </>
  );
}
